// Sygnał życia ewidencji czasu pracy.
//
// Świadomie NIE ma tu własnego śledzenia aktywności ani auto-wylogowania —
// robi to `idleActivity.js`, wspólny dla całego panelu. Dwa równoległe liczniki
// oznaczałyby dwa okna ostrzeżenia, dwa wylogowania i dwa źródła prawdy o tym,
// czy ktoś pracuje. Tutaj zostaje samo pytanie: bić sygnał czy milczeć.
//
// Zasada, na której stoi poprawność ewidencji: sygnał leci TYLKO wtedy, gdy od
// ostatniej aktywności minęło mniej niż próg pauzy. Po przekroczeniu progu
// milkniemy, backend domyka sesję na ostatnim sygnale, a powrót użytkownika
// otwiera nową. Gdybyśmy bili bezwarunkowo, karta otwarta na noc dopisałaby
// kilkanaście godzin „pracy".

import axios from 'axios';
import { idleMs } from './idleActivity';

const HEARTBEAT_MS = 60000;         // sygnał życia co minutę
const TICK_MS = 5000;               // z jaką rozdzielczością sprawdzamy warunki
const DISABLED_RECHECK_MS = 600000; // ewidencja wyłączona — pytamy co 10 min

const state = {
  running: false,
  timer: null,
  lastHeartbeatAt: 0,
  nextEnabledCheckAt: 0,
  enabled: false,
  todayMinutes: 0,
  config: null,
  handlers: {},
};

function authConfig() {
  return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
}

function sameConfig(a, b) {
  if (!a || !b) return false;
  return a.idleMinutes === b.idleMinutes
    && a.autoLogoutMinutes === b.autoLogoutMinutes
    && a.warnBeforeLogoutMinutes === b.warnBeforeLogoutMinutes;
}

/** Stan dla widoków (strona ewidencji, ewentualny wskaźnik w nagłówku). */
export function workTimeState() {
  return {
    enabled: state.enabled,
    todayMinutes: state.todayMinutes,
    config: state.config ? { ...state.config } : null,
  };
}

async function sendHeartbeat() {
  try {
    const res = await axios.post(process.env.VUE_APP_ROOT_API + 'work-time/heartbeat', {}, authConfig());
    const data = res.data?.data || {};
    state.enabled = data.enabled === true;
    state.lastHeartbeatAt = Date.now();
    if (typeof data.todayMinutes === 'number') state.todayMinutes = data.todayMinutes;
    if (!state.enabled) state.nextEnabledCheckAt = Date.now() + DISABLED_RECHECK_MS;

    // Progi przestawiamy tylko przy realnej zmianie — inaczej restartowalibyśmy
    // licznik bezczynności co minutę, gubiąc jego fazę.
    if (data.config && state.enabled && !sameConfig(state.config, data.config)) {
      state.config = data.config;
      if (state.handlers.onConfig) state.handlers.onConfig(data.config);
    } else if (data.config) {
      state.config = data.config;
    }
  } catch (e) {
    // Mrugnięcie sieci nie może wywalić panelu, a cisza jest bezpieczna:
    // backend domknie sesję na ostatnim sygnale.
    if (e.response?.status === 401) stopWorkTimeHeartbeat();
  }
}

/** Świadome zamknięcie sesji: LOGOUT (klik) albo TIMEOUT (bezczynność). */
export async function closeWorkSession(reason) {
  if (!localStorage.getItem('token')) return;
  try {
    await axios.post(process.env.VUE_APP_ROOT_API + 'work-time/close', { reason }, authConfig());
  } catch (e) {
    // Wylogowania nie blokujemy błędem zamykania sesji.
  }
}

function tick() {
  if (!state.running) return;

  // Ewidencja wyłączona: nic nie zapisujemy (art. 22³ KP), tylko rzadko
  // sprawdzamy, czy nie została włączona w ustawieniach.
  if (!state.enabled) {
    if (Date.now() >= state.nextEnabledCheckAt) sendHeartbeat();
    return;
  }

  const pauseMs = (state.config?.idleMinutes || 10) * 60000;
  if (idleMs() >= pauseMs) return;      // pauza — to nie jest czas pracy

  if (Date.now() - state.lastHeartbeatAt >= HEARTBEAT_MS) sendHeartbeat();
}

/**
 * Start sygnału życia.
 * @param {{onConfig?:Function}} handlers `onConfig` dostaje progi z backendu,
 *        żeby przestawić na nie wspólny licznik bezczynności.
 */
export function startWorkTimeHeartbeat(handlers = {}) {
  if (state.running) return;
  if (!localStorage.getItem('token')) return;
  state.running = true;
  state.handlers = handlers;
  state.lastHeartbeatAt = 0;
  state.nextEnabledCheckAt = 0;
  sendHeartbeat();
  state.timer = setInterval(tick, TICK_MS);
}

export function stopWorkTimeHeartbeat() {
  if (!state.running) return;
  state.running = false;
  clearInterval(state.timer);
  state.timer = null;
}
